import { isValidQuestionnaireName } from './isValidQuestionnaireName'
import type { MonarchQuestionnaireName } from '../constants/questionnaires/questionnaireMap'
import { getIncense } from '../error-handling/getIncense'

export interface QuestionnaireFromUrlResult {
  /**
   * The validated questionnaire name, or null if not found or invalid
   */
  questionnaire: MonarchQuestionnaireName | null
  /**
   * True if a questionnaire parameter was explicitly provided in the URL but was invalid.
   * This allows callers to distinguish between:
   * - No parameter provided (hasInvalidParam: false, questionnaire: null)
   * - Invalid parameter provided (hasInvalidParam: true, questionnaire: null)
   */
  hasInvalidParam: boolean
}

/**
 * Extracts and validates the questionnaire parameter from the current URL.
 *
 * This function reads the 'questionnaire' query parameter from the URL and validates
 * it against the available questionnaire names. It works in both browser and SSR environments.
 *
 * @returns An object containing:
 *   - questionnaire: The validated questionnaire name, or null if not found/invalid
 *   - hasInvalidParam: True if a questionnaire param exists but is invalid
 *
 * @example
 * ```typescript
 * // URL: https://example.com?questionnaire=shortQuiz
 * const result = getQuestionnaireFromUrl()
 * // Returns: { questionnaire: 'shortQuiz', hasInvalidParam: false }
 *
 * // URL: https://example.com?questionnaire=invalid
 * const result = getQuestionnaireFromUrl()
 * // Returns: { questionnaire: null, hasInvalidParam: true }
 *
 * // URL: https://example.com (no parameter)
 * const result = getQuestionnaireFromUrl()
 * // Returns: { questionnaire: null, hasInvalidParam: false }
 * ```
 */
export const getQuestionnaireFromUrl = (): QuestionnaireFromUrlResult => {
  // Guard: Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return { questionnaire: null, hasInvalidParam: false }
  }

  try {
    const urlParams = new URLSearchParams(window.location.search)
    const questionnaireParam = urlParams.get('questionnaire')

    if (questionnaireParam === null) {
      return { questionnaire: null, hasInvalidParam: false }
    }

    if (questionnaireParam === '') {
      return { questionnaire: null, hasInvalidParam: true }
    }

    if (isValidQuestionnaireName(questionnaireParam)) {
      return { questionnaire: questionnaireParam, hasInvalidParam: false }
    }

    // Invalid questionnaire name was explicitly provided
    return { questionnaire: null, hasInvalidParam: true }
  } catch (error) {
    const incense = getIncense()
    incense(error)
      .captureDetails({
        name: 'QuestionnaireUrlParsingError',
        message: 'Failed to parse questionnaire parameter from URL',
      })
      .sendError()
    return { questionnaire: null, hasInvalidParam: false }
  }
}

export const setQuestionnaireInUrl = (questionnaire: MonarchQuestionnaireName): void => {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)

  url.searchParams.set('questionnaire', questionnaire)
  window.history.replaceState(window.history.state, '', url)
}
